// src/components/auth/ProfileModal.tsx
// Self-service "Edit Profile": name, avatar, email change (re-verifies the
// new address), mobile change (OTP), and account deletion — same
// step-based-modal pattern as MfaSettingsModal.tsx.
import { useRef, useState } from 'react';
import { UserCircle2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { userService } from 'services/userService';
import { authService } from 'services/authService';
import { updateUser, logout } from 'store/slices/authSlice';
import { showToast } from 'store/slices/toastSlice';
import { useNavigate } from 'react-router-dom';
import FieldError from 'components/ui/FieldError';

type Section = 'main' | 'mobile-request' | 'mobile-verify' | 'delete';

export default function ProfileModal({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [section, setSection] = useState<Section>('main');
  const [apiError, setApiError] = useState('');
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState(user?.name || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [mobile, setMobile] = useState(user?.mobile || '');
  const [mobileStatus, setMobileStatus] = useState('');
  const [otpCode, setOtpCode] = useState('');

  const [deletePassword, setDeletePassword] = useState('');

  const [mobileReqAttempted, setMobileReqAttempted] = useState(false);
  const [mobileVerifyAttempted, setMobileVerifyAttempted] = useState(false);
  const [deleteAttempted, setDeleteAttempted] = useState(false);

  const mobileReqErrors: { mobile?: string } = {};
  if (!mobile.trim()) mobileReqErrors.mobile = 'Enter a mobile number';
  else if (mobile.trim().length < 7) mobileReqErrors.mobile = 'Enter a valid mobile number';

  const mobileVerifyErrors: { otpCode?: string } = {};
  if (otpCode.trim().length !== 6) mobileVerifyErrors.otpCode = 'Enter the 6-digit code';

  const deleteErrors: { deletePassword?: string } = {};
  if (!deletePassword) deleteErrors.deletePassword = 'Password is required to confirm deletion';

  const inp = (hasError?: boolean) =>
    `w-full px-3 py-2 bg-[var(--om-panel)] border rounded-lg ` +
    `text-[var(--om-text)] text-xs outline-none focus:border-blue-500 placeholder:text-[var(--om-text-dim)] ${
      hasError ? 'border-red-500' : 'border-[var(--om-border)]'
    }`;
  const btn =
    'w-full py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-[var(--om-border)] disabled:text-[var(--om-text-dim)] ' +
    'text-white rounded-xl font-bold text-xs transition-all';
  const btnGhost =
    'flex-1 py-2 bg-[var(--om-card)] border border-[var(--om-border)] rounded-xl text-[var(--om-text-muted)] ' +
    'text-xs hover:text-[var(--om-text)] transition-all';

  async function handleSaveName() {
    setApiError('');
    setBusy(true);
    try {
      await userService.updateProfile(name.trim());
      dispatch(updateUser({ name: name.trim() }));
      dispatch(showToast('✅ Profile updated'));
    } catch (err: any) {
      setApiError(err?.response?.data?.message || 'Failed to update profile');
    } finally {
      setBusy(false);
    }
  }

  async function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setApiError('');
    setUploadingAvatar(true);
    try {
      const res = await userService.uploadAvatar(file);
      dispatch(updateUser({ avatar: res.avatar }));
      dispatch(showToast('✅ Avatar updated'));
    } catch (err: any) {
      setApiError(err?.response?.data?.message || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleRequestMobileChange() {
    setMobileReqAttempted(true);
    if (Object.keys(mobileReqErrors).length > 0) return;
    setApiError('');
    setMobileStatus('');
    setBusy(true);
    try {
      const res = await userService.requestMobileChange(mobile.trim());
      setMobileStatus(res.message);
      setMobileVerifyAttempted(false);
      setSection('mobile-verify');
    } catch (err: any) {
      setApiError(err?.response?.data?.message || 'Failed to request mobile verification');
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyMobile() {
    setMobileVerifyAttempted(true);
    if (Object.keys(mobileVerifyErrors).length > 0) return;
    setApiError('');
    setBusy(true);
    try {
      const res = await userService.verifyMobile(otpCode.trim());
      dispatch(updateUser({ mobile: res.mobile, isMobileVerified: true }));
      dispatch(showToast('✅ Mobile number verified'));
      setSection('main');
      setOtpCode('');
    } catch (err: any) {
      setApiError(err?.response?.data?.message || 'Invalid code');
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteAccount() {
    setDeleteAttempted(true);
    if (Object.keys(deleteErrors).length > 0) return;
    setApiError('');
    setBusy(true);
    try {
      await userService.deleteAccount(deletePassword);
      try { await authService.logout(); } catch { /* ignore */ }
      dispatch(logout());
      navigate('/login');
    } catch (err: any) {
      setApiError(err?.response?.data?.message || 'Failed to delete account');
      setBusy(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[90]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100]
                      w-full max-w-sm bg-[var(--om-panel)] border border-[var(--om-border)] rounded-2xl
                      p-6 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="inline-flex items-center gap-1.5 text-sm font-black text-[var(--om-text)]"><UserCircle2 className="w-4 h-4" strokeWidth={2} /> Edit Profile</h2>
          <button onClick={onClose} className="text-[var(--om-text-dim)] hover:text-[var(--om-text)] text-lg leading-none">×</button>
        </div>

        {apiError && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-red-400 text-[11px] mb-3">
            {apiError}
          </div>
        )}

        {section === 'main' && (
          <div className="space-y-4">
            {/* Avatar */}
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-[var(--om-card)] border border-[var(--om-border)] overflow-hidden flex items-center justify-center shrink-0">
                {user?.avatar ? <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" /> : <UserCircle2 className="w-8 h-8 text-[var(--om-text-dim)]" strokeWidth={1.5} />}
              </div>
              <div className="flex-1">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" id="avatar-input" />
                <label
                  htmlFor="avatar-input"
                  className="inline-block cursor-pointer px-3 py-1.5 bg-[var(--om-card)] border border-[var(--om-border)] rounded-lg text-[11px] text-[var(--om-text-muted)] hover:text-[var(--om-text)] transition-all"
                >
                  {uploadingAvatar ? 'Uploading...' : 'Change Photo'}
                </label>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">Name</label>
              <input value={name} onChange={e => setName(e.target.value)} className={inp()} placeholder="Your name" />
            </div>
            <button onClick={handleSaveName} disabled={busy || !name.trim() || name.trim() === user?.name} className={btn}>
              {busy ? 'Saving...' : 'Save Name'}
            </button>

            <div className="border-t border-[var(--om-border)] pt-3 space-y-2">
              {/* Email */}
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-[var(--om-text-dim)]">Email: <span className="text-[var(--om-text-muted)]">{user?.email}</span></span>
                {user?.isEmailVerified === false && <span className="text-amber-400 font-bold">Unverified</span>}
              </div>

              {/* Mobile */}
              <div className="flex items-center justify-between text-[11px] mt-2">
                <span className="text-[var(--om-text-dim)]">Mobile: <span className="text-[var(--om-text-muted)]">{user?.mobile || 'Not set'}</span></span>
                {user?.mobile && !user?.isMobileVerified && <span className="text-amber-400 font-bold">Unverified</span>}
              </div>
              <button onClick={() => { setApiError(''); setMobileStatus(''); setMobileReqAttempted(false); setSection('mobile-request'); }}
                className="w-full py-2 bg-[var(--om-card)] border border-[var(--om-border)] rounded-xl text-[var(--om-text-muted)] text-xs hover:text-[var(--om-text)] transition-all">
                {user?.mobile ? 'Change Mobile' : 'Add Mobile Number'}
              </button>
            </div>

            {!user?.isAdmin && (
              <button onClick={() => { setApiError(''); setDeleteAttempted(false); setSection('delete'); }}
                className="w-full py-2 bg-red-600/10 border border-red-500/25 rounded-xl text-red-400 text-xs hover:bg-red-600/20 transition-all mt-2">
                Delete Account
              </button>
            )}
          </div>
        )}

        {section === 'mobile-request' && (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">Mobile Number</label>
              <input value={mobile} onChange={e => setMobile(e.target.value)} placeholder="+91 98765 43210" className={inp(mobileReqAttempted && !!mobileReqErrors.mobile)} />
              <FieldError message={mobileReqAttempted ? mobileReqErrors.mobile : undefined} />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setSection('main')} className={btnGhost}>Back</button>
              <button onClick={handleRequestMobileChange} disabled={busy} className={`flex-1 ${btn}`}>
                {busy ? 'Sending...' : 'Send Code'}
              </button>
            </div>
          </div>
        )}

        {section === 'mobile-verify' && (
          <div className="space-y-3">
            {mobileStatus && (
              <div className="bg-blue-500/10 border border-blue-500/25 rounded-lg px-3 py-2 text-blue-400 text-[11px]">{mobileStatus}</div>
            )}
            <div>
              <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">Enter 6-digit code</label>
              <input value={otpCode} onChange={e => setOtpCode(e.target.value)} placeholder="123456" autoFocus
                className={`${inp(mobileVerifyAttempted && !!mobileVerifyErrors.otpCode)} text-center tracking-[0.3em] font-mono`} />
              <FieldError message={mobileVerifyAttempted ? mobileVerifyErrors.otpCode : undefined} />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setMobileVerifyAttempted(false); setSection('mobile-request'); }} className={btnGhost}>Back</button>
              <button onClick={handleVerifyMobile} disabled={busy} className={`flex-1 ${btn}`}>
                {busy ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        )}

        {section === 'delete' && (
          <div className="space-y-3">
            <div className="bg-red-500/10 border border-red-500/25 rounded-xl px-3 py-2 text-red-400 text-[11px]">
              This deactivates your account and signs you out everywhere. This cannot be undone from the app — contact an admin to restore it.
            </div>
            <div>
              <label className="text-[11px] text-[var(--om-text-dim)] font-bold uppercase tracking-widest block mb-1">Confirm Password</label>
              <input value={deletePassword} onChange={e => setDeletePassword(e.target.value)} type="password" placeholder="••••••••" className={inp(deleteAttempted && !!deleteErrors.deletePassword)} />
              <FieldError message={deleteAttempted ? deleteErrors.deletePassword : undefined} />
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setSection('main')} className={btnGhost}>Cancel</button>
              <button onClick={handleDeleteAccount} disabled={busy}
                className="flex-1 py-2 bg-red-600/80 hover:bg-red-500 disabled:bg-[var(--om-border)] text-white rounded-xl font-bold text-xs transition-all">
                {busy ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
